/**
 * R268ff · Feb 2026 — GST Sales shared constants + helpers.
 *
 * Split out of `GstSales.jsx` to keep the page shell slim and let
 * heavy child dialogs (composer, detail, payment) import a common
 * source of truth.
 */
import {
  FileText, ArrowRight, Receipt, Wallet, RefreshCw, ShoppingCart,
  TrendingUp, Download, Repeat, IndianRupee, ShieldCheck, Trash2,
} from "lucide-react";
import { fmtMoney } from "@/lib/currency";

export const DOC_TYPES = [
  { key: "estimate",        label: "Quotations",       icon: FileText },
  { key: "sales_order",     label: "Sales orders",     icon: ArrowRight },
  { key: "invoice",         label: "Invoices",         icon: Receipt },
  { key: "credit_note",     label: "Credit notes",     icon: RefreshCw },
  { key: "vendor_bill",     label: "Vendor bills",     icon: ShoppingCart },
  { key: "debit_note",      label: "Debit notes",      icon: RefreshCw },
  { key: "payment_receipt", label: "Payments",         icon: Wallet },
  // Non-doc-type "views" — handled separately (not passed to list endpoint)
  { key: "__itc",           label: "ITC ledger",       icon: TrendingUp,  isView: true },
  { key: "__reports",       label: "GSTR reports",     icon: Download,    isView: true },
  { key: "__recurring",     label: "Recurring",        icon: Repeat,      isView: true },
  // R268x · currency roll-up + FX reconciliation.
  { key: "__currency",      label: "Currency",         icon: IndianRupee, isView: true },
  { key: "__fx_recon",      label: "FX recon",         icon: ShieldCheck, isView: true },
  // Feb 2026 · Soft-deleted drafts within the 30-day retention window.
  { key: "__trash",         label: "Deleted",          icon: Trash2,      isView: true },
];

export const STATUS_STYLES = {
  draft:          "bg-muted text-muted-foreground",
  sent:           "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-200",
  accepted:       "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200",
  approved:       "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200",
  rejected:       "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-200",
  superseded:     "bg-neutral-200 text-neutral-700 line-through dark:bg-neutral-800 dark:text-neutral-300",
  converted:      "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-200",
  confirmed:      "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-200",
  partially_paid: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-200",
  paid:           "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200",
  overdue:        "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-200",
  void:           "bg-neutral-200 text-neutral-600 line-through",
  cancelled:      "bg-neutral-200 text-neutral-600 line-through",
  recorded:       "bg-emerald-100 text-emerald-700",
  issued:         "bg-blue-100 text-blue-700",
  applied:        "bg-emerald-100 text-emerald-700",
  active:         "bg-emerald-100 text-emerald-700",
  paused:         "bg-amber-100 text-amber-700",
  completed:      "bg-violet-100 text-violet-700",
};

export const EMPTY_LINE = {
  hsn_sac: "", description: "", unit: "Nos",
  qty: 1, rate: 0, discount_pct: 0, gst_rate: 18, cess_rate: 0,
};

export function inr(n) {
  const num = Number(n || 0);
  return num.toLocaleString("en-IN", {
    maximumFractionDigits: 2, minimumFractionDigits: 2,
  });
}

export function StatusPill({ status }) {
  const cls = STATUS_STYLES[status] || "bg-muted text-muted-foreground";
  return (
    <span
      data-testid={`gst-status-${status}`}
      className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full ${cls}`}
    >
      {String(status || "").replace(/_/g, " ")}
    </span>
  );
}

export function PreviewRow({ label, value, bold, currency = "INR" }) {
  return (
    <div className={`flex justify-between tabular-nums ${bold ? "font-bold text-base" : ""}`}>
      <span className={bold ? "" : "text-muted-foreground"}>{label}</span>
      <span>{fmtMoney(value, currency)}</span>
    </div>
  );
}
